import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, BookOpen, Eye, ImagePlus, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { WorkItemPicker } from '@/components/work/WorkItemPicker';
import { WorkInstructionsPanel } from '@/components/work/WorkInstructionsPanel';
import { QuietEmpty, StoredImage } from './shared';
import {
  uploadInstructionImage,
  useDeleteInstruction,
  useDeleteStep,
  useReorderSteps,
  useSaveInstruction,
  useSaveStep,
  useWorkInstructions,
} from '@/features/workMode/hooks';
import { describeWorkError, formatDuration } from '@/features/workMode/api';
import { compressImage } from '@/features/workMode/images';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { WorkInstructionStep, WorkInstructionWithSteps } from '@/features/workMode/types';
import { formatRelative } from '@/lib/formatters';
import { stationHref } from '@/features/workMode/links';

interface InstructionsTabProps {
  lookups: WorkLookups;
  canManage: boolean;
}

const ANY_STATION = '__any__';

function parseSeconds(text: string): number | null | 'invalid' {
  const t = text.trim();
  if (!t) return null;
  const mmss = /^(\d+):([0-5]?\d)$/.exec(t);
  if (mmss) return Number(mmss[1]) * 60 + Number(mmss[2]);
  const mins = /^(\d+(?:\.\d+)?)\s*(m|min|mins)?$/.exec(t);
  if (mins) return Math.round(Number(mins[1]) * 60);
  const secs = /^(\d+)\s*s$/.exec(t);
  if (secs) return Number(secs[1]);
  return 'invalid';
}

function secondsText(seconds: number | null): string {
  if (!seconds) return '';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Illustrated work instructions: one set per item (optionally per station),
 * each a list of steps with a photo. Drafts stay hidden from the benches
 * until published.
 */
export function InstructionsTab({ lookups, canManage }: InstructionsTabProps) {
  const { data: sets = [], isLoading } = useWorkInstructions();
  // `?set=<id>` deep-links one set (the station page sends people here).
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('set');
  const setSelectedId = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('set', id);
    else p.delete('set');
    setParams(p, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(() => sets.find((s) => s.id === selectedId) ?? null, [sets, selectedId]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sets
      .map((s) => ({ set: s, item: lookups.itemName(s.item_id) ?? '', sku: lookups.itemSku(s.item_id) ?? '' }))
      .filter((r) => !q || r.set.title.toLowerCase().includes(q) || r.item.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q))
      .sort((a, b) => a.item.localeCompare(b.item) || a.set.title.localeCompare(b.set.title));
  }, [sets, query, lookups]);

  if (isLoading || lookups.loading) return <Skeleton className="h-80 rounded-2xl" />;

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="flex min-h-0 flex-col rounded-2xl border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border p-3">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search item or title" className="h-9" />
          {canManage && (
            <Button size="sm" onClick={() => setCreating(true)} aria-label="New instruction set">
              <Plus className="h-4 w-4" />
              New
            </Button>
          )}
        </div>
        {list.length === 0 ? (
          <div className="p-3">
            <QuietEmpty>{sets.length === 0 ? 'No instruction sets yet.' : 'Nothing matches.'}</QuietEmpty>
          </div>
        ) : (
          <ul className="max-h-[70vh] divide-y divide-border overflow-y-auto">
            {list.map(({ set, item, sku }) => (
              <li key={set.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(set.id)}
                  className={cn('flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left hover:bg-secondary/60', selectedId === set.id && 'bg-info-soft/20')}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{set.title}</span>
                    <Badge variant={set.is_published ? 'success-soft' : 'muted'}>{set.is_published ? 'Live' : 'Draft'}</Badge>
                  </div>
                  <span className="truncate text-xs text-muted-foreground">
                    {item} {sku && <span className="opacity-70">{sku}</span>} · {set.workstation_id ? lookups.stationName(set.workstation_id) : 'Any station'} · {set.steps.length} step
                    {set.steps.length === 1 ? '' : 's'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="min-h-[320px]">
        {selected ? (
          <InstructionEditor key={selected.id} set={selected} lookups={lookups} canManage={canManage} onDeleted={() => setSelectedId(null)} />
        ) : (
          <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center">
            <BookOpen className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">Pick a set to edit</p>
            </div>
          </div>
        )}
      </section>

      <NewInstructionDialog open={creating} onOpenChange={setCreating} lookups={lookups} onCreated={setSelectedId} />
    </div>
  );
}

function NewInstructionDialog({
  open,
  onOpenChange,
  lookups,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lookups: WorkLookups;
  onCreated: (id: string) => void;
}) {
  const save = useSaveInstruction();
  const [itemId, setItemId] = useState<string | null>(null);
  const [stationId, setStationId] = useState<string>(ANY_STATION);
  const [title, setTitle] = useState('');
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (open) {
      setItemId(null);
      setStationId(ANY_STATION);
      setTitle('');
    }
  }, [open]);

  const pickItem = (id: string | null) => {
    setItemId(id);
    if (id && !title.trim()) setTitle(`How to build ${lookups.itemName(id)}`);
  };

  const create = () => {
    if (!itemId) {
      toast.error('Pick the item these instructions are for.');
      return;
    }
    save.mutate(
      { itemId, workstationId: stationId === ANY_STATION ? null : stationId, title, isPublished: false },
      {
        onSuccess: (row) => {
          onCreated(row.id);
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New instruction set</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Item</Label>
            <div className="flex items-center gap-2">
              <div className="flex min-h-10 flex-1 items-center rounded-md border border-border bg-background px-3 text-sm">
                {itemId ? (
                  <span className="truncate">
                    {lookups.itemName(itemId)} <span className="text-muted-foreground">{lookups.itemSku(itemId)}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Choose an item</span>
                )}
              </div>
              <Button variant="outline" onClick={() => setPicking(true)}>
                Choose
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Station</Label>
            <Select value={stationId} onValueChange={setStationId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_STATION}>Any station</SelectItem>
                {lookups.stationList
                  .filter((s) => s.is_active)
                  .map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-title">Title</Label>
            <Input id="new-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="How to build the Base" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={create} disabled={save.isPending || !title.trim()}>
            Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
      <WorkItemPicker open={picking} onOpenChange={setPicking} stationId={stationId === ANY_STATION ? null : stationId} currentItemId={itemId} onPick={pickItem} title="Which item are these instructions for?" />
    </Dialog>
  );
}

function InstructionEditor({
  set,
  lookups,
  canManage,
  onDeleted,
}: {
  set: WorkInstructionWithSteps;
  lookups: WorkLookups;
  canManage: boolean;
  onDeleted: () => void;
}) {
  const save = useSaveInstruction();
  const remove = useDeleteInstruction();
  const reorder = useReorderSteps();
  const deleteStep = useDeleteStep();
  const [title, setTitle] = useState(set.title);
  const [stationId, setStationId] = useState(set.workstation_id ?? ANY_STATION);
  const [summary, setSummary] = useState(set.summary ?? '');
  const [safety, setSafety] = useState(set.safety_notes ?? '');
  const [target, setTarget] = useState(secondsText(set.target_seconds));
  const [published, setPublished] = useState(set.is_published);
  const [editingStep, setEditingStep] = useState<WorkInstructionStep | 'new' | null>(null);
  const [preview, setPreview] = useState(false);

  const dirty =
    title !== set.title ||
    (stationId === ANY_STATION ? null : stationId) !== set.workstation_id ||
    summary !== (set.summary ?? '') ||
    safety !== (set.safety_notes ?? '') ||
    target !== secondsText(set.target_seconds) ||
    published !== set.is_published;

  const parsedTarget = parseSeconds(target);

  const saveMeta = () => {
    if (parsedTarget === 'invalid') {
      toast.error('Target looks odd — use mm:ss or minutes.');
      return;
    }
    if (published && set.steps.length === 0) {
      toast.error('Add at least one step before publishing.');
      return;
    }
    save.mutate(
      {
        id: set.id,
        itemId: set.item_id,
        workstationId: stationId === ANY_STATION ? null : stationId,
        title,
        summary,
        safetyNotes: safety,
        targetSeconds: parsedTarget,
        isPublished: published,
      },
      {
        onSuccess: () => toast.success(published ? 'Saved and live on the benches.' : 'Draft saved.'),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  const move = (idx: number, dir: -1 | 1) => {
    const ids = set.steps.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorder.mutate({ orderedIds: ids }, { onError: (e) => toast.error(describeWorkError(e)) });
  };

  const removeStep = (s: WorkInstructionStep) => {
    if (!window.confirm(`Delete step ${s.step_number} "${s.title}"?`)) return;
    deleteStep.mutate(
      { id: s.id, imagePath: s.image_path },
      {
        onSuccess: () => {
          const rest = set.steps.filter((x) => x.id !== s.id).map((x) => x.id);
          if (rest.length) reorder.mutate({ orderedIds: rest });
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  const removeSet = () => {
    if (!window.confirm(`Delete "${set.title}" and all ${set.steps.length} steps? Benches will stop showing it immediately.`)) return;
    remove.mutate(
      { id: set.id },
      {
        onSuccess: () => {
          toast.success('Instruction set deleted.');
          onDeleted();
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {lookups.itemName(set.item_id)} · v{set.version} · updated {formatRelative(set.updated_at)}
              {set.workstation_id && (
                <>
                  {' · '}
                  <Link to={stationHref(set.workstation_id, 'instructions')} className="normal-case tracking-normal text-info hover:underline">
                    {lookups.stationName(set.workstation_id)}
                  </Link>
                </>
              )}
            </p>
            <h3 className="truncate text-lg font-medium text-foreground">{set.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPreview(true)} disabled={set.steps.length === 0}>
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            {canManage && (
              <Button variant="ghost" size="sm" className="text-fresh-salmon-dark" onClick={removeSet} disabled={remove.isPending}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="is-title">Title</Label>
            <Input id="is-title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canManage} />
          </div>
          <div className="space-y-1.5">
            <Label>Station</Label>
            <Select value={stationId} onValueChange={setStationId} disabled={!canManage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_STATION}>Any station</SelectItem>
                {lookups.stationList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="is-summary">Summary</Label>
            <Textarea id="is-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} placeholder="What this builds and what 'done' looks like." disabled={!canManage} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="is-safety">Safety notes</Label>
            <Textarea id="is-safety" value={safety} onChange={(e) => setSafety(e.target.value)} rows={2} placeholder="PPE, pinch points, hot surfaces…" disabled={!canManage} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="is-target">Target time per unit</Label>
            <Input id="is-target" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="mm:ss" className={cn(parsedTarget === 'invalid' && 'border-destructive')} disabled={!canManage} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">Published</p>
            </div>
            <Switch checked={published} onCheckedChange={setPublished} disabled={!canManage} />
          </div>
        </div>
        {canManage && (
          <div className="mt-4 flex justify-end">
            <Button onClick={saveMeta} disabled={!dirty || save.isPending}>
              Save
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h4 className="text-sm font-medium text-foreground">
            Steps <span className="text-muted-foreground">({set.steps.length})</span>
          </h4>
          {canManage && (
            <Button size="sm" onClick={() => setEditingStep('new')}>
              <Plus className="h-4 w-4" />
              Add step
            </Button>
          )}
        </header>
        {set.steps.length === 0 ? (
          <div className="p-4">
            <QuietEmpty>No steps yet.</QuietEmpty>
          </div>
        ) : (
          <ol className="divide-y divide-border">
            {set.steps.map((s, i) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">{s.step_number}</span>
                {s.image_path ? (
                  <StoredImage bucket="work-instructions" path={s.image_path} alt={s.title} className="h-14 w-20 shrink-0" />
                ) : (
                  <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground">
                    <ImagePlus className="h-4 w-4" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{s.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {s.body || 'No description'}
                    {s.est_seconds ? ` · ~${formatDuration(s.est_seconds)}` : ''}
                    {s.caution ? ' · ⚠ caution' : ''}
                  </p>
                </div>
                {canManage && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0 || reorder.isPending} aria-label="Move up">
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === set.steps.length - 1 || reorder.isPending} aria-label="Move down">
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditingStep(s)} aria-label="Edit step">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-fresh-salmon-dark" onClick={() => removeStep(s)} disabled={deleteStep.isPending} aria-label="Delete step">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>

      {editingStep !== null && (
        <StepDialog key={editingStep === 'new' ? 'new' : editingStep.id} set={set} step={editingStep === 'new' ? null : editingStep} onClose={() => setEditingStep(null)} />
      )}

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="h-[85vh] max-w-3xl p-0">
          <DialogTitle className="sr-only">Preview</DialogTitle>
          <WorkInstructionsPanel instruction={set} className="h-full rounded-2xl border-0" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Mounted only while open (keyed by step id) so a background refetch never wipes a half-filled form. */
function StepDialog({ set, step, onClose }: { set: WorkInstructionWithSteps; step: WorkInstructionStep | null; onClose: () => void }) {
  const { profile } = useAuth();
  const saveStep = useSaveStep();
  const [title, setTitle] = useState(step?.title ?? '');
  const [body, setBody] = useState(step?.body ?? '');
  const [caution, setCaution] = useState(step?.caution ?? '');
  const [est, setEst] = useState(secondsText(step?.est_seconds ?? null));
  const [file, setFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const onOpenChange = (o: boolean) => {
    if (!o) onClose();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const parsedEst = parseSeconds(est);
  const existingImage = !removeImage && !file ? (step?.image_path ?? null) : null;

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Give the step a title.');
      return;
    }
    if (parsedEst === 'invalid') {
      toast.error('Estimated time looks odd — use mm:ss.');
      return;
    }
    if (!profile?.organization_id) return;
    setBusy(true);
    try {
      let imagePath: string | null = removeImage ? null : (step?.image_path ?? null);
      if (file) {
        const blob = await compressImage(file, 1600, 0.85);
        const upload = new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
        imagePath = await uploadInstructionImage(profile.organization_id, set.id, upload);
      }
      await saveStep.mutateAsync({
        id: step?.id,
        instructionId: set.id,
        stepNumber: step?.step_number ?? set.steps.length + 1,
        title,
        body,
        caution,
        imagePath,
        estSeconds: parsedEst,
      });
      toast.success(step ? 'Step updated.' : 'Step added.');
      onOpenChange(false);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{step ? `Step ${step.step_number}` : `Step ${set.steps.length + 1}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="relative h-32 w-44 shrink-0 overflow-hidden rounded-xl border border-border bg-secondary">
              {previewUrl ? (
                <img src={previewUrl} alt="" className="h-full w-full object-cover" />
              ) : existingImage ? (
                <StoredImage bucket="work-instructions" path={existingImage} alt="" className="h-full w-full rounded-none" />
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-secondary/70">
                  <ImagePlus className="h-6 w-6" />
                  <span className="text-xs">Add photo</span>
                </button>
              )}
              {(previewUrl || existingImage) && (
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setRemoveImage(true);
                  }}
                  aria-label="Remove photo"
                  className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-humus/80 text-white hover:bg-humus"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
                <ImagePlus className="h-4 w-4" />
                {previewUrl || existingImage ? 'Replace photo' : 'Choose photo'}
              </Button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f) {
                  setFile(f);
                  setRemoveImage(false);
                }
                e.target.value = '';
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="step-title">Title</Label>
            <Input id="step-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Press the bearing into the housing" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="step-body">Details</Label>
            <Textarea id="step-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Torque, orientation, what to check before moving on." />
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
            <div className="space-y-1.5">
              <Label htmlFor="step-caution">Caution (optional)</Label>
              <Input id="step-caution" value={caution} onChange={(e) => setCaution(e.target.value)} placeholder="Pinch point — keep fingers clear of the press" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="step-est">Est. time</Label>
              <Input id="step-est" value={est} onChange={(e) => setEst(e.target.value)} placeholder="mm:ss" className={cn(parsedEst === 'invalid' && 'border-destructive')} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !title.trim()}>
            {busy ? 'Saving…' : step ? 'Save step' : 'Add step'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
